/**
 * Excel Export Utility
 * Generates proper .xlsx-compatible files using HTML table format
 * that Excel opens natively with formatting, colors, and proper encoding.
 */

interface CellStyle {
  bold?: boolean
  color?: string
  bgColor?: string
  align?: 'left' | 'center' | 'right'
  fontSize?: number
  fontFamily?: string
  border?: boolean
  numberFormat?: string
  width?: number
  colSpan?: number
}

interface ExcelCell {
  value: string | number
  style?: CellStyle
}

interface ExcelRow {
  cells: ExcelCell[]
  height?: number
}

interface ExcelSheet {
  name: string
  columns?: { width: number }[]
  rows: ExcelRow[]
}

// Color palette
const COLORS = {
  headerBg: '#1F2937',
  headerText: '#FFFFFF',
  sectionBg: '#F3F4F6',
  sectionText: '#111827',
  subtotalBg: '#EFF6FF',
  subtotalText: '#1E40AF',
  totalBg: '#1F2937',
  totalText: '#FFFFFF',
  borderColor: '#E5E7EB',
  lightGray: '#F9FAFB',
  white: '#FFFFFF',
  greenBg: '#ECFDF5',
  greenText: '#065F46',
  redBg: '#FEF2F2',
  redText: '#991B1B',
  yellowBg: '#FFFBEB',
  yellowText: '#92400E',
  blueBg: '#EFF6FF',
  blueText: '#1E40AF',
}

function escapeHtml(str: string | number): string {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function cellToHtml(cell: ExcelCell): string {
  const s = cell.style || {}
  const styles: string[] = []

  if (s.bold) styles.push('font-weight:700')
  if (s.color) styles.push(`color:${s.color}`)
  if (s.bgColor) styles.push(`background-color:${s.bgColor}`)
  if (s.align) styles.push(`text-align:${s.align}`)
  if (s.fontSize) styles.push(`font-size:${s.fontSize}pt`)
  if (s.fontFamily) styles.push(`font-family:${s.fontFamily}`)
  if (s.border !== false) {
    styles.push(`border:1px solid ${COLORS.borderColor}`)
  }
  styles.push('padding:6px 10px')
  styles.push('vertical-align:middle')
  styles.push('white-space:nowrap')

  const colSpan = s.colSpan ? ` colspan="${s.colSpan}"` : ''
  const styleAttr = styles.length > 0 ? ` style="${styles.join(';')}"` : ''

  return `<td${colSpan}${styleAttr}>${escapeHtml(cell.value)}</td>`
}

function sheetToHtml(sheet: ExcelSheet): string {
  let html = ''

  // Column widths
  if (sheet.columns && sheet.columns.length > 0) {
    html += '<colgroup>'
    sheet.columns.forEach((col) => {
      html += `<col width="${col.width}">`
    })
    html += '</colgroup>'
  }

  sheet.rows.forEach((row) => {
    const heightAttr = row.height ? ` height="${row.height}"` : ''
    html += `<tr${heightAttr}>`
    row.cells.forEach((cell) => {
      html += cellToHtml(cell)
    })
    html += '</tr>'
  })

  return html
}

function generateExcelHtml(sheets: ExcelSheet[]): string {
  // For single sheet, generate simple HTML table
  // For multiple sheets, we use the Worksheet XML format

  let tablesHtml = ''

  sheets.forEach((sheet, idx) => {
    const display = idx === 0 ? '' : ' style="page-break-before:always"'
    tablesHtml += `<div${display}>`
    if (sheets.length > 1) {
      tablesHtml += `<h2 style="font-family:'Segoe UI',Arial,sans-serif;font-size:14pt;color:#1F2937;margin:20px 0 10px 0;padding-bottom:5px;border-bottom:2px solid #1F2937;">${escapeHtml(sheet.name)}</h2>`
    }
    tablesHtml +=
      '<table cellspacing="0" cellpadding="0" style="border-collapse:collapse;font-family:\'Segoe UI\',Arial,sans-serif;font-size:10pt;">'
    tablesHtml += sheetToHtml(sheet)
    tablesHtml += '</table>'
    tablesHtml += '</div>'
  })

  return `
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="UTF-8">
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
<!--[if gte mso 9]>
<xml>
<x:ExcelWorkbook>
<x:ExcelWorksheets>
${sheets.map((s, i) => `<x:ExcelWorksheet><x:Name>${escapeHtml(s.name)}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet>`).join('')}
</x:ExcelWorksheets>
</x:ExcelWorkbook>
</xml>
<![endif]-->
<style>
  td { mso-number-format:\\@; }
  .num { mso-number-format:"#,##0.00"; }
</style>
</head>
<body>
${tablesHtml}
</body>
</html>`
}

function downloadExcel(html: string, filename: string) {
  const blob = new Blob(['\uFEFF' + html], {
    type: 'application/vnd.ms-excel;charset=utf-8',
  })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// ─── Helper builders ───

function headerRow(texts: string[], colWidths?: number[]): ExcelRow {
  return {
    cells: texts.map((text, i) => ({
      value: text,
      style: {
        bold: true,
        color: COLORS.headerText,
        bgColor: COLORS.headerBg,
        align: i === 0 ? 'left' : 'right',
        fontSize: 10,
        border: true,
      },
    })),
    height: 28,
  }
}

function dataRow(
  values: (string | number)[],
  opts?: { even?: boolean; alignments?: ('left' | 'center' | 'right')[] },
): ExcelRow {
  const even = opts?.even ?? false
  return {
    cells: values.map((val, i) => ({
      value: val,
      style: {
        bgColor: even ? COLORS.lightGray : COLORS.white,
        align: opts?.alignments?.[i] || (i === 0 ? 'left' : 'right'),
        fontSize: 10,
        border: true,
      },
    })),
  }
}

function titleRow(text: string, colSpan: number): ExcelRow {
  return {
    cells: [
      {
        value: text,
        style: {
          bold: true,
          fontSize: 14,
          color: COLORS.sectionText,
          bgColor: COLORS.white,
          colSpan,
          border: false,
        },
      },
    ],
    height: 30,
  }
}

function sectionRow(text: string, colSpan: number): ExcelRow {
  return {
    cells: [
      {
        value: text,
        style: {
          bold: true,
          fontSize: 11,
          color: COLORS.blueText,
          bgColor: COLORS.blueBg,
          colSpan,
          border: true,
        },
      },
    ],
    height: 26,
  }
}

function summaryRow(
  label: string,
  value: string,
  colSpan: number,
  highlight?: boolean,
): ExcelRow {
  return {
    cells: [
      {
        value: label,
        style: {
          bold: true,
          fontSize: 10,
          color: highlight ? COLORS.totalText : COLORS.sectionText,
          bgColor: highlight ? COLORS.totalBg : COLORS.sectionBg,
          colSpan: colSpan - 1,
          border: true,
        },
      },
      {
        value: value,
        style: {
          bold: true,
          fontSize: highlight ? 12 : 10,
          color: highlight ? COLORS.totalText : COLORS.sectionText,
          bgColor: highlight ? COLORS.totalBg : COLORS.sectionBg,
          align: 'right',
          border: true,
        },
      },
    ],
    height: highlight ? 28 : 22,
  }
}

function emptyRow(colSpan: number): ExcelRow {
  return {
    cells: [
      {
        value: '',
        style: { border: false, colSpan, bgColor: COLORS.white },
      },
    ],
    height: 10,
  }
}

function fmtCurrency(amount: number): string {
  return `RD$ ${amount.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// ─── PUBLIC EXPORT FUNCTIONS ───

export interface SalesReportData {
  filters: {
    company: string
    branch: string
    dateFrom: string
    dateTo: string
  }
  summary: {
    totalOrders: number
    subtotal: number
    tax: number
    totalDiscount: number
    totalSubsidy: number
    exemptSales: number
    taxableSales: number
    totalSales: number
    averageOrder: number
  }
  salesByCompany: {
    company: string
    orders: number
    subtotal: number
    tax: number
    exempt: number
    taxable: number
    total: number
  }[]
  salesByBranch: {
    company: string
    branch: string
    orders: number
    subtotal: number
    tax: number
    exempt: number
    taxable: number
    total: number
  }[]
  orderDetails: {
    orderNo: string
    orderDate: string
    name: string
    company: string
    branch: string
    product: string
    quantity: number
    subtotal: number
    tax: number
    discount: number
    subsidy: number
    taxExempt: boolean
    total: number
    status: string
  }[]
}

export function exportSalesReport(data: SalesReportData) {
  const { filters, summary, salesByCompany, salesByBranch, orderDetails } = data
  const cols = 8

  const rows: ExcelRow[] = []

  // Title
  rows.push(titleRow('Reporte de Ventas - La Cochera', cols))
  rows.push(emptyRow(cols))

  // Metadata
  rows.push({
    cells: [
      {
        value: 'Fecha de Generación:',
        style: { bold: true, fontSize: 9, color: '#6B7280', border: false },
      },
      {
        value: new Date().toLocaleString('es-DO'),
        style: {
          fontSize: 9,
          color: '#374151',
          border: false,
          colSpan: cols - 1,
        },
      },
    ],
  })
  rows.push({
    cells: [
      {
        value: 'Empresa:',
        style: { bold: true, fontSize: 9, color: '#6B7280', border: false },
      },
      {
        value: filters.company,
        style: { fontSize: 9, color: '#374151', border: false },
      },
      {
        value: 'Sucursal:',
        style: { bold: true, fontSize: 9, color: '#6B7280', border: false },
      },
      {
        value: filters.branch,
        style: {
          fontSize: 9,
          color: '#374151',
          border: false,
          colSpan: cols - 3,
        },
      },
    ],
  })
  rows.push({
    cells: [
      {
        value: 'Rango de Fechas:',
        style: { bold: true, fontSize: 9, color: '#6B7280', border: false },
      },
      {
        value: `${filters.dateFrom || 'Todas'} — ${filters.dateTo || 'Todas'}`,
        style: {
          fontSize: 9,
          color: '#374151',
          border: false,
          colSpan: cols - 1,
        },
      },
    ],
  })
  rows.push(emptyRow(cols))

  // ── Resumen General ──
  rows.push(sectionRow('📊 Resumen General', cols))
  rows.push(
    summaryRow('Total de Pedidos', summary.totalOrders.toString(), cols),
  )
  rows.push(
    summaryRow('Subtotal (sin ITBIS)', fmtCurrency(summary.subtotal), cols),
  )
  rows.push(summaryRow('ITBIS (18%)', fmtCurrency(summary.tax), cols))
  rows.push(summaryRow('Descuentos', fmtCurrency(summary.totalDiscount), cols))
  rows.push(summaryRow('Subsidios', fmtCurrency(summary.totalSubsidy), cols))
  rows.push(summaryRow('Total Exento', fmtCurrency(summary.exemptSales), cols))
  rows.push(
    summaryRow('Total Gravado', fmtCurrency(summary.taxableSales), cols),
  )
  rows.push(
    summaryRow('Total de Ventas', fmtCurrency(summary.totalSales), cols, true),
  )
  rows.push(
    summaryRow('Promedio por Pedido', fmtCurrency(summary.averageOrder), cols),
  )
  rows.push(emptyRow(cols))

  // ── Ventas por Empresa ──
  rows.push(sectionRow('🏢 Ventas por Empresa', cols))
  rows.push(
    headerRow([
      'Empresa',
      'Pedidos',
      'Subtotal',
      'ITBIS',
      'Exento',
      'Gravado',
      'Total',
      '',
    ]),
  )
  salesByCompany.forEach((s, i) => {
    rows.push(
      dataRow(
        [
          s.company,
          s.orders,
          fmtCurrency(s.subtotal),
          fmtCurrency(s.tax),
          fmtCurrency(s.exempt),
          fmtCurrency(s.taxable),
          fmtCurrency(s.total),
          '',
        ],
        {
          even: i % 2 === 0,
          alignments: [
            'left',
            'center',
            'right',
            'right',
            'right',
            'right',
            'right',
            'left',
          ],
        },
      ),
    )
  })
  if (salesByCompany.length === 0) {
    rows.push({
      cells: [
        {
          value: 'No hay datos',
          style: {
            colSpan: cols,
            align: 'center',
            color: '#9CA3AF',
            fontSize: 10,
            border: true,
          },
        },
      ],
    })
  }
  rows.push(emptyRow(cols))

  // ── Ventas por Sucursal ──
  rows.push(sectionRow('📍 Ventas por Sucursal', cols))
  rows.push(
    headerRow([
      'Empresa',
      'Sucursal',
      'Pedidos',
      'Subtotal',
      'ITBIS',
      'Exento',
      'Gravado',
      'Total',
    ]),
  )
  salesByBranch.forEach((s, i) => {
    rows.push(
      dataRow(
        [
          s.company,
          s.branch,
          s.orders,
          fmtCurrency(s.subtotal),
          fmtCurrency(s.tax),
          fmtCurrency(s.exempt),
          fmtCurrency(s.taxable),
          fmtCurrency(s.total),
        ],
        {
          even: i % 2 === 0,
          alignments: [
            'left',
            'left',
            'center',
            'right',
            'right',
            'right',
            'right',
            'right',
          ],
        },
      ),
    )
  })
  if (salesByBranch.length === 0) {
    rows.push({
      cells: [
        {
          value: 'No hay datos',
          style: {
            colSpan: cols,
            align: 'center',
            color: '#9CA3AF',
            fontSize: 10,
            border: true,
          },
        },
      ],
    })
  }
  rows.push(emptyRow(cols))

  // ── Detalle de Pedidos ──
  const detailCols = 14
  rows.push(sectionRow('📋 Detalle de Pedidos', detailCols))
  rows.push(
    headerRow([
      'No. Orden',
      'Fecha',
      'Cliente',
      'Empresa',
      'Sucursal',
      'Producto',
      'Cant.',
      'Subtotal',
      'ITBIS',
      'Descuento',
      'Subsidio',
      'Exento',
      'Total',
      'Estado',
    ]),
  )
  orderDetails.forEach((order, i) => {
    rows.push(
      dataRow(
        [
          order.orderNo,
          order.orderDate,
          order.name,
          order.company,
          order.branch,
          order.product,
          order.quantity,
          fmtCurrency(order.subtotal),
          fmtCurrency(order.tax),
          fmtCurrency(order.discount),
          fmtCurrency(order.subsidy),
          order.taxExempt ? 'Sí' : 'No',
          fmtCurrency(order.total),
          order.status,
        ],
        {
          even: i % 2 === 0,
          alignments: [
            'left',
            'left',
            'left',
            'left',
            'left',
            'left',
            'center',
            'right',
            'right',
            'right',
            'right',
            'center',
            'right',
            'center',
          ],
        },
      ),
    )
  })

  const sheet: ExcelSheet = {
    name: 'Reporte de Ventas',
    columns: [
      { width: 120 },
      { width: 160 },
      { width: 150 },
      { width: 160 },
      { width: 140 },
      { width: 250 },
      { width: 60 },
      { width: 120 },
      { width: 100 },
      { width: 100 },
      { width: 100 },
      { width: 70 },
      { width: 120 },
      { width: 100 },
    ],
    rows,
  }

  const html = generateExcelHtml([sheet])
  downloadExcel(
    html,
    `reporte-ventas-${new Date().toISOString().split('T')[0]}.xls`,
  )
}

// ── Orders Export ──

export interface OrdersExportData {
  filters: {
    company: string
    branch: string
    dateFilter: string
  }
  orders: {
    orderNo: string
    orderDate: string
    name: string
    company: string
    branch: string
    product: string
    quantity: number
    subtotal: number
    tax: number
    discount: number
    subsidy: number
    total: number
    status: string
    labelStatus: string
    taxExempt: boolean
  }[]
}

export function exportOrdersToExcel(data: OrdersExportData) {
  const { filters, orders } = data
  const cols = 15
  const rows: ExcelRow[] = []

  rows.push(titleRow('Órdenes - Cricket', cols))
  rows.push(emptyRow(cols))

  // Metadata
  rows.push({
    cells: [
      {
        value: 'Fecha de Exportación:',
        style: { bold: true, fontSize: 9, color: '#6B7280', border: false },
      },
      {
        value: new Date().toLocaleString('es-DO'),
        style: {
          fontSize: 9,
          color: '#374151',
          border: false,
          colSpan: cols - 1,
        },
      },
    ],
  })
  rows.push({
    cells: [
      {
        value: 'Empresa:',
        style: { bold: true, fontSize: 9, color: '#6B7280', border: false },
      },
      {
        value: filters.company,
        style: { fontSize: 9, color: '#374151', border: false },
      },
      {
        value: 'Sucursal:',
        style: { bold: true, fontSize: 9, color: '#6B7280', border: false },
      },
      {
        value: filters.branch,
        style: { fontSize: 9, color: '#374151', border: false },
      },
      {
        value: 'Fecha:',
        style: { bold: true, fontSize: 9, color: '#6B7280', border: false },
      },
      {
        value: filters.dateFilter,
        style: {
          fontSize: 9,
          color: '#374151',
          border: false,
          colSpan: cols - 5,
        },
      },
    ],
  })
  rows.push({
    cells: [
      {
        value: 'Total de Órdenes:',
        style: { bold: true, fontSize: 9, color: '#6B7280', border: false },
      },
      {
        value: orders.length.toString(),
        style: {
          bold: true,
          fontSize: 9,
          color: '#1F2937',
          border: false,
          colSpan: cols - 1,
        },
      },
    ],
  })
  rows.push(emptyRow(cols))

  // Header
  rows.push(
    headerRow([
      'No. Orden',
      'Fecha',
      'Cliente',
      'Empresa',
      'Sucursal',
      'Producto',
      'Cant.',
      'Subtotal',
      'ITBIS',
      'Descuento',
      'Subsidio',
      'Total',
      'Estado',
      'Etiqueta',
      'Exento',
    ]),
  )

  // Data
  orders.forEach((order, i) => {
    rows.push(
      dataRow(
        [
          order.orderNo,
          order.orderDate,
          order.name,
          order.company,
          order.branch,
          order.product,
          order.quantity,
          fmtCurrency(order.subtotal),
          fmtCurrency(order.tax),
          fmtCurrency(order.discount),
          fmtCurrency(order.subsidy),
          fmtCurrency(order.total),
          order.status,
          order.labelStatus,
          order.taxExempt ? 'Sí' : 'No',
        ],
        {
          even: i % 2 === 0,
          alignments: [
            'left',
            'left',
            'left',
            'left',
            'left',
            'left',
            'center',
            'right',
            'right',
            'right',
            'right',
            'right',
            'center',
            'center',
            'center',
          ],
        },
      ),
    )
  })

  const sheet: ExcelSheet = {
    name: 'Órdenes',
    columns: [
      { width: 80 },
      { width: 160 },
      { width: 150 },
      { width: 160 },
      { width: 140 },
      { width: 250 },
      { width: 50 },
      { width: 110 },
      { width: 100 },
      { width: 100 },
      { width: 100 },
      { width: 110 },
      { width: 100 },
      { width: 80 },
      { width: 60 },
    ],
    rows,
  }

  const html = generateExcelHtml([sheet])
  downloadExcel(html, `ordenes-${new Date().toISOString().split('T')[0]}.xls`)
}

// ── Activity Log Export ──

export interface ActivityLogExportData {
  logs: {
    timestamp: string
    user: string
    action: string
    details: string
    type: string
  }[]
}

export function exportActivityLogToExcel(data: ActivityLogExportData) {
  const { logs } = data
  const cols = 5
  const rows: ExcelRow[] = []

  rows.push(titleRow('Registro de Actividad - Cricket', cols))
  rows.push(emptyRow(cols))
  rows.push({
    cells: [
      {
        value: 'Fecha de Exportación:',
        style: { bold: true, fontSize: 9, color: '#6B7280', border: false },
      },
      {
        value: new Date().toLocaleString('es-DO'),
        style: { fontSize: 9, color: '#374151', border: false },
      },
      {
        value: 'Total de Registros:',
        style: { bold: true, fontSize: 9, color: '#6B7280', border: false },
      },
      {
        value: logs.length.toString(),
        style: {
          bold: true,
          fontSize: 9,
          color: '#1F2937',
          border: false,
          colSpan: 2,
        },
      },
    ],
  })
  rows.push(emptyRow(cols))

  rows.push(
    headerRow(['Fecha y Hora', 'Usuario', 'Acción', 'Detalles', 'Tipo']),
  )

  logs.forEach((log, i) => {
    rows.push(
      dataRow([log.timestamp, log.user, log.action, log.details, log.type], {
        even: i % 2 === 0,
        alignments: ['left', 'left', 'left', 'left', 'center'],
      }),
    )
  })

  const sheet: ExcelSheet = {
    name: 'Registro de Actividad',
    columns: [
      { width: 180 },
      { width: 150 },
      { width: 200 },
      { width: 300 },
      { width: 100 },
    ],
    rows,
  }

  const html = generateExcelHtml([sheet])
  downloadExcel(
    html,
    `registro-actividad-${new Date().toISOString().split('T')[0]}.xls`,
  )
}
